import { Commodity } from "@/pages/sy/models/Commodity";
import {
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { ManualAutoButton } from "@/shared/data-entries/ManualAutoButton";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { ChargeLayer } from "@/pages/sy/models/ChargeLayer";
import {
  useUpsertChargeLayerMutation,
  useDeleteChargeLayerMutation,
} from "@/pages/sy/hooks/queries/useChargeMutations";
import { getErrorMessage } from "@/shared/hooks/getErrorMessage";
import { ErrorModal } from "@dexteel/mesf-core";
import { getBgColorWithAlpha } from "../../../utils/colorUtils";
import { ChargesColors } from "@/pages/sy/theme/colors";
import { CommoditySelector } from "./CommoditySelector";
import { WeightInput } from "./WeightInput";

interface ChargeLayerComponentProps {
  layer: ChargeLayer;
  commodities: Commodity[];
  disabled: boolean;
  setNotificationMessage: (message: string) => void;
  initialEditMode?: boolean;
  onEditComplete?: () => void;
  isNewLayer?: boolean;
  onCancelNewLayer?: (layerId: number) => void;
  totalWeight?: number;
  maxSelectableLayerNumber: number;
  onLayerNumberChange: (layer: ChargeLayer, newLayerNumber: number) => void;
  siblingCommodityIds: number[];
  isLastInGroup?: boolean;
}

export const ChargeLayerComponent = ({
  layer,
  commodities,
  disabled,
  setNotificationMessage,
  onEditComplete,
  isNewLayer = false,
  onCancelNewLayer,
  totalWeight = 0,
  maxSelectableLayerNumber,
  onLayerNumberChange,
  siblingCommodityIds,
  isLastInGroup = false,
}: ChargeLayerComponentProps) => {
  // Local validation error (for client-side checks)
  const [validationError, setValidationError] = useState<string>("");

  // === TanStack Query Mutations ===
  const upsertChargeLayerMutation = useUpsertChargeLayerMutation();
  const deleteChargeLayerMutation = useDeleteChargeLayerMutation();

  const [error, setError] = useState("");
  const onMutationError = (err: unknown) => setError(getErrorMessage(err));

  // Combined loading state for UI (replaces manual isLoading state)
  const isLoading =
    upsertChargeLayerMutation.isPending || deleteChargeLayerMutation.isPending;

  const enabledCommodities = useMemo(() => {
    return commodities.filter((c) => c.IsEnable);
  }, [commodities]);

  const { control, reset, watch, getValues } = useForm<ChargeLayer>({
    mode: "onChange",
    defaultValues: layer,
  });

  const watchedWeight = watch("Weight");
  const watchedCommodityId = watch("CommodityId");

  const currentCommodity = useMemo(() => {
    return enabledCommodities.find((c) => c.CommodityId === watchedCommodityId);
  }, [enabledCommodities, watchedCommodityId]);

  const commodityColor = useMemo(() => {
    return currentCommodity?.CommodityColor || ChargesColors.STATUS_INACTIVE;
  }, [currentCommodity]);

  const percentage = useMemo(() => {
    if (!totalWeight || !watchedWeight) return 0;
    return Math.round((watchedWeight / totalWeight) * 100);
  }, [watchedWeight, totalWeight]);

  const bgColor = getBgColorWithAlpha(commodityColor, 0.1);
  const inputBgColor = getBgColorWithAlpha(commodityColor, 0.15);

  const saveLayer = (data: ChargeLayer) => {
    setValidationError("");

    if (data.Weight < 0) {
      setValidationError("Weight must be greater than or equal to 0.");
      return;
    }

    upsertChargeLayerMutation.mutate(
      {
        ...data,
        IsWeightManual: true,
      },
      {
        onSuccess: () => {
          setNotificationMessage("Charge layer saved successfully");
          if (onEditComplete) {
            onEditComplete();
          }
        },
        onError: onMutationError,
      },
    );
  };

  const handleDelete = () => {
    if (!layer.ChargeLayerId) return;

    deleteChargeLayerMutation.mutate(layer.ChargeLayerId, {
      onSuccess: () => {
        setNotificationMessage("Charge layer deleted successfully");
      },
      onError: onMutationError,
    });
  };

  const handleCancel = () => {
    if (isNewLayer && layer.ChargeLayerId && onCancelNewLayer) {
      onCancelNewLayer(layer.ChargeLayerId);
    }
  };

  const handleWeightBlur = () => {
    const formData = getValues();
    saveLayer(formData);
  };

  const handleCommodityChange = (commodityId: number | null) => {
    const formData = getValues();
    formData.CommodityId = commodityId;
    saveLayer(formData);
  };

  // Reset form when layer data changes (from server via SignalR refetch)
  useEffect(() => {
    reset(layer);
  }, [layer.ChargeLayerId, layer.CommodityId, layer.Weight, layer.LayerNumber]);

  return (
    <div
      style={{
        position: "relative",
        backgroundColor: bgColor,
        padding: "8px 0",
      }}
    >
      <div
        style={{
          position: "absolute",
          bottom: isLastInGroup ? "3px" : 0,
          left: 0,
          height: "3px",
          width: `${percentage}%`,
          backgroundColor: commodityColor,
          zIndex: 1,
        }}
      />

      <Grid container alignItems="center" spacing={0}>
        <Grid item xs={2} style={{ padding: "0 8px", textAlign: "center" }}>
          <Controller
            name="LayerNumber"
            control={control}
            render={({ field }) => (
              <FormControl variant="outlined" size="small" fullWidth>
                <Select
                  value={field.value}
                  onChange={(e) =>
                    onLayerNumberChange(layer, Number(e.target.value))
                  }
                  disabled={disabled}
                  style={{
                    backgroundColor: "white",
                    borderColor: commodityColor,
                    minHeight: "40px",
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                      },
                    },
                  }}
                >
                  {Array.from(
                    { length: maxSelectableLayerNumber },
                    (_, i) => i + 1,
                  ).map((num) => (
                    <MenuItem
                      key={num}
                      value={num}
                      style={{ justifyContent: "center" }}
                    >
                      {num}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid>

        <Grid item xs={6} style={{ padding: "0 8px", textAlign: "center" }}>
          <Controller
            name="CommodityId"
            control={control}
            render={({ field }) => (
              <CommoditySelector
                value={field.value as number}
                onChange={handleCommodityChange}
                disabled={disabled}
                bgColor={inputBgColor}
                borderColor={commodityColor}
                excludedCommodityIds={siblingCommodityIds}
                disableClearable
              />
            )}
          />
        </Grid>

        <Grid item xs={2} style={{ padding: "0 8px", textAlign: "center" }}>
          <Controller
            name="Weight"
            control={control}
            render={({ field }) => (
              <WeightInput
                value={field.value || 0}
                onChange={field.onChange}
                disabled={disabled}
                bgColor={inputBgColor}
                borderColor={commodityColor}
                onBlur={handleWeightBlur}
              />
            )}
          />
        </Grid>

        <Grid
          item
          xs={2}
          sx={{
            padding: "0 8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 0.5,
          }}
        >
          {layer.IsManualEdit && <ManualAutoButton isManual />}
          <Tooltip
            title={layer.IsLayerAudit ? "This layer has audit data" : ""}
          >
            <span>
              <IconButton
                onClick={handleDelete}
                color="secondary"
                disabled={disabled || layer.IsLayerAudit}
                size="small"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Grid>
      </Grid>
      <ErrorModal
        error={error || validationError}
        onHide={() => {
          setError("");
          setValidationError("");
        }}
      />
    </div>
  );
};
